import { PREP_TOUR_END, PrepTourAnchorId, PrepTourDefinition, PrepTourStep } from 'src/@prep/shared/tour';
import { WORKFLOW_ANCHORS as A } from '../guided-workflow.anchors';
import {
  WORKFLOW_JOURNEY_GUIDE_ID, WorkflowFileStatus, WorkflowPath, WorkflowUploadOutcome,
} from '../guided-workflow.types';
import { WorkflowTranslate } from '../services/guided-workflow-i18n.service';

export const WORKFLOW_JOURNEY_VERSION = 1;

/** Live app state and navigation the journey needs (see GuidedWorkflowService). */
export interface WorkflowJourneyHooks {
  onUploadPage: () => boolean;
  inRemediation: () => boolean;
  samplesAvailable: () => boolean;
  fileCount: () => number;
  uploading: () => boolean;
  /** The onboarding confirmation dialog is open. */
  confirmOpen: () => boolean;
  /** The onboarding file was confirmed and its upload is about to start. */
  sampleAccepted: () => boolean;
  outcome: () => WorkflowUploadOutcome | null;
  path: () => WorkflowPath | null;
  fileStatus: () => WorkflowFileStatus;
  fileName: () => string;
  choosePath: (path: WorkflowPath) => void;
  /** Forget the previous upload result (a retry starts clean). */
  clearOutcome: () => void;
  /** Forget the saved workflow and begin again. */
  startOver: () => void;
  /** The workflow is over (nothing left to resume). */
  finish: () => void;
  goToStep: (id: string) => void;
  /** Go to the dashboard (if elsewhere) and wait for the add button. */
  openDashboard: () => Promise<void>;
  /** Go to the upload screen (if elsewhere) and wait for it to be ready. */
  openUpload: () => Promise<void>;
  /** Go to the dashboard (if elsewhere) and wait for the uploaded file's card. */
  openDashboardCard: () => Promise<void>;
  waitForAnchor: (id: PrepTourAnchorId) => Promise<boolean>;
}

/**
 * "Start work", layout leg — one task from the dashboard to the uploaded
 * file's card (the workbench leg continues in the remediation screen):
 *
 *   [resume] → add (dashboard + button; skipped on the upload screen) →
 *   choice ──(own file)──▶ drop → uploadBtn ──▶ uploading
 *          └─(onboarding)─▶ sample → confirm ──▶ uploading
 *   uploading ──(dashboard)──▶ card ──(file opened)──▶ end
 *             ├─(straight into remediation)──▶ end
 *             ├─(service request)──▶ orders
 *             └─(failed / abandoned)──▶ drop | sample
 *
 * Every stop waits for the real control: the guide advances on what the user
 * did (route, queue length, upload result), never on a Next press, except the
 * choice and the explanatory stops.
 */
export function buildWorkflowJourneyGuide(
  t: WorkflowTranslate,
  h: WorkflowJourneyHooks,
  opts: { resume?: boolean } = {},
): PrepTourDefinition {
  const k = (s: string, p?: Record<string, unknown>): string => t(s, p);
  const uploadStarted = (): boolean => h.uploading() || h.outcome() !== null;
  /** Nothing queued, nothing running, nothing confirmed: the user backed out. */
  const abandoned = (): boolean =>
    !h.confirmOpen() && !h.sampleAccepted() && !h.uploading() && h.fileCount() === 0;
  const retryStep = (): string => (h.path() === 'sample' ? 'sample' : 'drop');
  /** A stop that waits for the real control and offers only "Change choice". */
  const waitsForUser: Partial<PrepTourStep> = {
    backButton: false, nextButton: false, skippable: false,
    skipTo: 'choice', skipLabel: k('changeChoice'), skipFillMode: 'outline',
  };

  const steps: PrepTourStep[] = [
    {
      id: 'add', target: A.dashboard.add, placement: 'left',
      title: k('steps.add.title'), body: k('steps.add.body'), actionNote: k('steps.add.note'),
      buttons: false, skippable: false,
      // On the upload screen the button does not exist: the stop is skipped.
      onEnter: () => (h.onUploadPage() ? undefined : h.openDashboard()),
      advance: { kind: 'predicate', satisfied: () => h.onUploadPage() },
    },
    {
      id: 'choice', hero: true, icon: 'note_add',
      title: k('steps.choice.title'),
      body: () => (h.samplesAvailable() ? k('steps.choice.body') : k('steps.choice.bodyNoSamples')),
      onEnter: () => h.openUpload(),
      action: {
        label: k('steps.choice.sample'), fillMode: 'outline',
        run: () => { h.choosePath('sample'); h.goToStep('sample'); },
        hidden: () => !h.samplesAvailable(),
      },
      nextLabel: k('steps.choice.own'), nextFillMode: 'outline',
      onNext: () => h.choosePath('new'),
      next: 'drop',
    },
    {
      id: 'drop', target: A.upload.dropzone, placement: 'bottom',
      title: k('steps.drop.title'), body: k('steps.drop.body'), actionNote: k('steps.drop.note'),
      ...waitsForUser,
      onEnter: async () => { h.clearOutcome(); await h.waitForAnchor(A.upload.dropzone); },
      advance: { kind: 'predicate', satisfied: () => h.fileCount() > 0 || uploadStarted() },
      next: 'uploadBtn',
      whenMissing: 'explain', missingText: k('steps.drop.missing'),
    },
    {
      id: 'uploadBtn', target: A.upload.uploadButton, placement: 'top',
      title: () => k('steps.uploadBtn.title', { count: h.fileCount() }),
      body: k('steps.uploadBtn.body'), actionNote: k('steps.uploadBtn.note'),
      ...waitsForUser,
      onEnter: async () => { await h.waitForAnchor(A.upload.uploadButton); },
      // Emptying the queue sends the user back to the drop area.
      advance: { kind: 'predicate', satisfied: () => uploadStarted() || h.fileCount() === 0 },
      next: () => (uploadStarted() ? 'uploading' : 'drop'),
      whenMissing: 'explain', missingText: k('steps.uploadBtn.missing'),
    },
    {
      id: 'sample', target: A.upload.samples, placement: 'top',
      title: k('steps.sample.title'), body: k('steps.sample.body'), actionNote: k('steps.sample.note'),
      ...waitsForUser,
      onEnter: async () => { h.clearOutcome(); await h.waitForAnchor(A.upload.samples); },
      advance: { kind: 'predicate', satisfied: () => h.confirmOpen() || h.sampleAccepted() || uploadStarted() },
      next: () => (h.confirmOpen() ? 'confirm' : 'uploading'),
      whenMissing: 'explain', missingText: k('steps.sample.missing'),
    },
    {
      // Missing button (dialog already answered) → the default `skip` lands
      // on `uploading`, which sorts out what happened.
      id: 'confirm', target: A.upload.confirm, placement: 'bottom',
      title: k('steps.confirm.title'), body: k('steps.confirm.body'), actionNote: k('steps.confirm.note'),
      buttons: false, skippable: false,
      onEnter: async () => { await h.waitForAnchor(A.upload.confirm); },
      advance: { kind: 'predicate', satisfied: () => !h.confirmOpen() },
      next: () => (h.sampleAccepted() || uploadStarted() ? 'uploading' : 'sample'),
    },
    {
      id: 'uploading', target: A.upload.area, placement: 'bottom', icon: 'cloud_upload',
      title: k('steps.uploading.title'), body: k('steps.uploading.body'),
      busy: () => true, skippable: false,
      onEnter: async () => { await h.waitForAnchor(A.upload.area); },
      // Out of the way from the moment the result is in (the app is navigating).
      hideWhile: () => h.outcome() !== null,
      advance: { kind: 'predicate', satisfied: () => h.outcome() !== null || abandoned() },
      next: () => {
        const outcome = h.outcome();
        switch (outcome ? outcome.kind : undefined) {
          case 'dashboard': return 'card';
          case 'remediation': return PREP_TOUR_END;
          case 'orders': return 'orders';
          default: return retryStep();
        }
      },
      whenMissing: 'explain',
    },
    {
      // No card for the file on this page of the list → `cardMissing`.
      id: 'card', target: A.dashboard.card, placement: 'right',
      title: () => {
        switch (h.fileStatus()) {
          case 'ready': return k('steps.card.titleReady');
          case 'failed': return k('steps.card.titleFailed');
          default: return k('steps.card.titleProcessing');
        }
      },
      body: () => {
        const file = h.fileName() || k('steps.card.yourFile');
        switch (h.fileStatus()) {
          case 'ready': return k('steps.card.bodyReady', { file });
          case 'failed': return k('steps.card.bodyFailed', { file });
          default: return k('steps.card.bodyProcessing', { file });
        }
      },
      actionNote: () => (h.fileStatus() === 'ready' ? k('steps.card.noteReady') : ''),
      busy: () => h.fileStatus() === 'processing',
      buttons: false, skippable: false,
      onEnter: () => h.openDashboardCard(),
      advance: { kind: 'predicate', satisfied: () => h.inRemediation() },
      next: PREP_TOUR_END,
    },
    {
      id: 'cardMissing', icon: 'arrow_forward',
      title: k('steps.cardMissing.title'), body: k('steps.cardMissing.body'),
      nextLabel: k('steps.cardMissing.next'), backButton: false,
      next: PREP_TOUR_END,
    },
    {
      id: 'orders',
      title: k('steps.orders.title'), body: k('steps.orders.body'),
      nextLabel: k('steps.orders.next'), backButton: false,
      onNext: () => h.finish(),
      next: PREP_TOUR_END,
    },
  ];

  if (opts.resume) {
    steps.unshift({
      id: 'resume', hero: true, icon: 'arrow_forward',
      title: k('resume.title'),
      body: () => (h.fileName() ? k('resume.body', { file: h.fileName() }) : k('resume.bodyNoName')),
      action: { label: k('resume.continue'), run: () => h.goToStep('card'), fillMode: 'solid' },
      nextLabel: k('resume.startOver'), nextFillMode: 'outline',
      onNext: () => h.startOver(),
      next: 'add',
    });
  }

  return {
    id: WORKFLOW_JOURNEY_GUIDE_ID,
    kind: 'recipe',
    version: WORKFLOW_JOURNEY_VERSION,
    title: k('title'),
    progress: 'none',
    welcome: opts.resume ? undefined : {
      icon: 'note_add', centered: true, closeButton: true,
      title: k('welcome.title'), body: k('welcome.body'),
      primary: k('welcome.primary'),
    },
    steps,
  };
}
